import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { DistributionKey, DistributionKeyUnit } from '../types'

const QUERY_KEY = 'distribution_keys'

export function useDistributionKeys(residenceId?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, residenceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('distribution_keys')
        .select('*')
        .eq('residence_id', residenceId!)
        .order('key_type')
        .order('name')

      if (error) throw error
      return data as DistributionKey[]
    },
    enabled: !!residenceId,
  })
}

export function useDistributionKey(id?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, 'detail', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('distribution_keys')
        .select(`
          *,
          distribution_key_units (
            *,
            unit:units (id, unit_number, stairwell, floor)
          )
        `)
        .eq('id', id!)
        .single()

      if (error) throw error
      return data as DistributionKey & { distribution_key_units: DistributionKeyUnit[] }
    },
    enabled: !!id,
  })
}

export function useGeneralDistributionKey(residenceId?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, 'general', residenceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('distribution_keys')
        .select('*')
        .eq('residence_id', residenceId!)
        .eq('key_type', 'general')
        .limit(1)
        .maybeSingle()

      if (error) throw error
      return data as DistributionKey | null
    },
    enabled: !!residenceId,
  })
}

export function useCreateDistributionKey() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: Omit<DistributionKey, 'id' | 'created_at' | 'updated_at'>) => {
      const { data: result, error } = await supabase
        .from('distribution_keys')
        .insert(data)
        .select()
        .single()
      if (error) throw error
      return result as DistributionKey
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, data.residence_id] })
    },
  })
}

export function useUpdateDistributionKey() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<DistributionKey> & { id: string }) => {
      const { data: result, error } = await supabase
        .from('distribution_keys')
        .update(data)
        .eq('id', id)
        .select()
        .single()
      if (error) throw error
      return result as DistributionKey
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

export function useUpdateDistributionKeyUnits() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      distributionKeyId,
      units,
      calculationBase,
    }: {
      distributionKeyId: string
      units: Array<{ unit_id: string; share_value?: number; is_included: boolean }>
      calculationBase: string
    }) => {
      // Supprimer les anciennes unités puis insérer les nouvelles
      const { error: deleteError } = await supabase
        .from('distribution_key_units')
        .delete()
        .eq('distribution_key_id', distributionKeyId)

      if (deleteError) throw deleteError

      // Insérer les nouvelles unités
      // share_value uniquement pour le mode "weighted", undefined sinon
      const { error: insertError } = await supabase.from('distribution_key_units').insert(
        units.map((u) => ({
          distribution_key_id: distributionKeyId,
          unit_id: u.unit_id,
          share_value: calculationBase === 'weighted' ? Math.round(u.share_value ?? 0) : undefined,
          is_included: u.is_included,
        }))
      )
      if (insertError) throw insertError
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

export function useCalculateDistribution() {
  return useMutation({
    mutationFn: async ({
      amount,
      distributionKeyId,
    }: {
      amount: number
      distributionKeyId: string
    }) => {
      const { data, error } = await supabase.rpc('calculate_distribution', {
        p_amount: amount,
        p_distribution_key_id: distributionKeyId,
      })
      if (error) throw error
      return data
    },
  })
}

export function useArchiveDistributionKey() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('distribution_keys')
        .update({ is_active: false })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

export function useDeleteDistributionKey() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      // D'abord supprimer les unités associées
      const { error: unitsError } = await supabase
        .from('distribution_key_units')
        .delete()
        .eq('distribution_key_id', id)
      if (unitsError) throw unitsError

      // Puis supprimer la clé
      const { error } = await supabase
        .from('distribution_keys')
        .delete()
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

export function useCheckDistributionKeyUsage() {
  return useMutation({
    mutationFn: async (id: string) => {
      // Vérifier si la clé a été utilisée (dans budget_lines ou autres)
      const { count, error } = await supabase
        .from('budget_lines')
        .select('*', { count: 'exact', head: true })
        .eq('distribution_key_id', id)

      if (error) throw error
      return (count ?? 0) > 0
    },
  })
}
